"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var checker_1 = require("../types/checker");
var type_1 = require("../types/type");
var formatter_1 = require("../types/formatter");
var genericConstraintSolver_1 = __importDefault(require("./genericConstraintSolver"));
var genericConstraintError_1 = __importDefault(require("./genericConstraintError"));
var generic_1 = require("./generic");
/**
 * The signature validator checks that the parameter types a function receives match those specified in its signature.
 */
var SignatureValidator = /** @class */ (function () {
    function SignatureValidator() {
    }
    /**
     * Checks if the given parameter types match the signature in the definition.
     *
     * If the definition has no signature, the parameter types are assumed to be correct and no errors are returned.
     *
     * @param parameters The types of the parameters given to the function.
     * @param definition The definition of the function to validate the provided parameters against.
     * @returns An array of errors that were encountered (if any) while validating the signature.
     */
    SignatureValidator.validateParameters = function (parameters, definition) {
        if (definition.signature) {
            return this.doValidate(parameters, definition.signature);
        }
        else {
            return { errors: [] };
        }
    };
    SignatureValidator.doValidate = function (parameters, signature) {
        if (this.isVariadicSignature(signature)) {
            return this.validateParametersVariadic(parameters, signature);
        }
        else {
            return this.validateParametersFixed(parameters, signature);
        }
    };
    SignatureValidator.isVariadicSignature = function (signature) {
        return signature.type === "variadic";
    };
    SignatureValidator.validateParametersFixed = function (parameters, signature) {
        if (parameters.length < signature.parameters.length) {
            return {
                errors: [{ message: "Function accepts at least " + signature.parameters.length + " parameter(s)" }],
            };
        }
        if (parameters.length > signature.parameters.length) {
            return {
                errors: [{ message: "Function accepts at most " + signature.parameters.length + " parameter(s)" }],
            };
        }
        var errors = [];
        var generics = new Map();
        for (var i = 0; i < parameters.length; i++) {
            var param = parameters[i];
            var signatureParamType = signature.parameters[i];
            if (generic_1.isGeneric(signatureParamType)) {
                var identifier = signatureParamType.genericIdentifier;
                if (!generics.has(identifier)) {
                    generics.set(identifier, new genericConstraintSolver_1.default(identifier));
                }
                try {
                    generics.get(identifier).addConstraint(signatureParamType, param);
                }
                catch (e) {
                    if (e instanceof genericConstraintError_1.default) {
                        errors.push({ parameterIndex: i, message: e.message });
                    }
                    else {
                        throw e;
                    }
                }
            }
            else if (!checker_1.typeMatchesType(param, signatureParamType)) {
                errors.push({ parameterIndex: i, message: "Type '" + formatter_1.formatType(param) + "' does not conform to expected type '" + formatter_1.formatType(signatureParamType) + "'" });
            }
        }
        generics.forEach(function (solver) {
            try {
                solver.resolveType();
            }
            catch (e) {
                errors.push({ message: e.message });
            }
        });
        var returns;
        try {
            returns = this.getReturnType(signature, generics);
        }
        catch (e) {
            returns = type_1.AnyType;
            errors.push({ message: e.message });
        }
        return {
            errors: errors,
            returns: returns,
        };
    };
    SignatureValidator.validateParametersVariadic = function (parameters, signature) {
        var _a, _b;
        if (parameters.length < signature.parameters.length) {
            return {
                errors: [{ message: "Function accepts at least " + signature.parameters.length + " parameter(s)" }],
            };
        }
        var expectedVariadicCount = Array.isArray(signature.variadicParameters) ? signature.variadicParameters.length : 1;
        var expectedAfterVariadicCount = (_b = (_a = signature.afterVariadicParameters) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0;
        var variadicCount = parameters.length - signature.parameters.length - expectedAfterVariadicCount;
        if (parameters.length > signature.parameters.length && variadicCount % expectedVariadicCount !== 0) {
            return {
                errors: [{ message: "Expected variadic parameter count (" + variadicCount + ") to be a multiple of signature variadic count (" + expectedVariadicCount + ")" }],
            };
        }
        var errors = [];
        var generics = new Map();
        for (var i = 0; i < parameters.length; i++) {
            var param = parameters[i];
            var paramType = void 0;
            if (i < signature.parameters.length) {
                paramType = signature.parameters[i];
            }
            else if (parameters.length - i <= expectedAfterVariadicCount) {
                paramType = signature.afterVariadicParameters[expectedAfterVariadicCount - (parameters.length - i)];
            }
            else if (Array.isArray(signature.variadicParameters)) {
                var variadicIndex = i - signature.parameters.length;
                var variadicSignatureIndex = variadicIndex % signature.variadicParameters.length;
                paramType = signature.variadicParameters[variadicSignatureIndex];
            }
            else {
                paramType = signature.variadicParameters;
            }
            if (generic_1.isGeneric(paramType)) {
                var identifier = paramType.genericIdentifier;
                if (!generics.has(identifier)) {
                    generics.set(identifier, new genericConstraintSolver_1.default(identifier));
                }
                generics.get(identifier).addConstraint(paramType, param);
            }
            else if (!checker_1.typeMatchesType(param, paramType)) {
                errors.push({ parameterIndex: i, message: "Type '" + formatter_1.formatType(param) + "' does not conform to expected type '" + formatter_1.formatType(paramType) + "'" });
            }
        }
        generics.forEach(function (solver) {
            try {
                solver.resolveType();
            }
            catch (e) {
                errors.push({ message: e.message });
            }
        });
        var returns;
        try {
            returns = this.getReturnType(signature, generics);
        }
        catch (e) {
            returns = type_1.AnyType;
            errors.push({ message: e.message });
        }
        return {
            errors: errors,
            returns: returns,
        };
    };
    SignatureValidator.getReturnType = function (signature, generics) {
        if (generic_1.isGeneric(signature.returns)) {
            var identifier = signature.returns.genericIdentifier;
            if (generics.has(identifier)) {
                return generics.get(identifier).resolvedType;
            }
            else {
                throw new genericConstraintError_1.default("No type could be resolved for generic type " + identifier + " used as return type");
            }
        }
        else {
            return signature.returns;
        }
    };
    return SignatureValidator;
}());
exports.default = SignatureValidator;
;
//# sourceMappingURL=signatureValidator.js.map